import React, { useMemo } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {
  parseDateOnly,
  fortnightEndFromStart,
  getRequirementFortnightStatus,
  calculateFortnightlyAmount,
  formatEffectiveRange,
} from '../../utils/requirementCalculations';
import { formatCurrency } from '../../utils/formatters';

const TIMELINE_COLS = 26;
const TIMELINE_CENTER = 12;

const relativeIndexForColumn = (fortnightIndex, col) => fortnightIndex + (col - TIMELINE_CENTER);

const startDateForRelativeIndex = (anchorStart, relativeIndex) => {
  const d = new Date(anchorStart);
  d.setDate(d.getDate() + relativeIndex * 14);
  return d;
};

const relativeIndexFromDate = (anchorStart, date) => {
  if (!date || !anchorStart) return null;
  const ms = date.getTime() - anchorStart.getTime();
  return Math.floor(ms / (14 * 24 * 60 * 60 * 1000));
};

const RequirementsTimeline = ({
  requirements,
  fortnightIndex,
  setFortnightIndex,
  anchorFortnightStart,
  fortnightStart,
  fortnightEnd,
}) => {
  const sortedReqs = useMemo(
    () => [...requirements].sort((a, b) => a.name.localeCompare(b.name)),
    [requirements]
  );

  const columnMeta = useMemo(() => {
    if (!anchorFortnightStart) return [];
    return Array.from({ length: TIMELINE_COLS }, (_, col) => {
      const rel = relativeIndexForColumn(fortnightIndex, col);
      const start = startDateForRelativeIndex(anchorFortnightStart, rel);
      return {
        col,
        rel,
        start,
        label: start.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
        isViewed: col === TIMELINE_CENTER,
      };
    });
  }, [anchorFortnightStart, fortnightIndex]);

  const bars = useMemo(() => {
    if (!anchorFortnightStart) return [];
    const windowStartRel = relativeIndexForColumn(fortnightIndex, 0);
    const windowEndRel = relativeIndexForColumn(fortnightIndex, TIMELINE_COLS - 1);

    return sortedReqs.map((req) => {
      const from = parseDateOnly(req.effective_from_date);
      const until = parseDateOnly(req.effective_until_date);

      let startRel = from ? relativeIndexFromDate(anchorFortnightStart, from) : windowStartRel;
      let endRel = until ? relativeIndexFromDate(anchorFortnightStart, until) : windowEndRel;

      if (startRel == null) startRel = windowStartRel;
      if (endRel == null) endRel = windowEndRel;

      const barStartCol = Math.max(0, Math.min(TIMELINE_COLS - 1, startRel - windowStartRel));
      const barEndCol = Math.max(barStartCol, Math.min(TIMELINE_COLS - 1, endRel - windowStartRel));
      const span = barEndCol - barStartCol + 1;

      const status =
        fortnightStart && fortnightEnd
          ? getRequirementFortnightStatus(req, fortnightStart, fortnightEnd)
          : 'active';

      return {
        req,
        leftPct: (barStartCol / TIMELINE_COLS) * 100,
        widthPct: (span / TIMELINE_COLS) * 100,
        status,
      };
    });
  }, [sortedReqs, anchorFortnightStart, fortnightIndex, fortnightStart, fortnightEnd]);

  if (!anchorFortnightStart) {
    return null;
  }

  return (
    <div className="requirements-timeline card border-0 shadow-sm mb-3">
      <div className="card-body py-3">
        <p className="text-muted small mb-2">
          Coverage by fortnight — click a column to change the viewed fortnight (also updates the
          Requirements tab when you switch back)
        </p>

        <div className="requirements-timeline-scroll">
          <div className="requirements-timeline-header">
            <div className="requirements-timeline-label-col" />
            <div className="requirements-timeline-track requirements-timeline-track-header">
              {columnMeta.map(({ col, label, isViewed, rel }) => (
                <button
                  key={col}
                  type="button"
                  className={`requirements-timeline-col-header ${isViewed ? 'is-viewed' : ''}`}
                  onClick={() => setFortnightIndex(rel)}
                  title={`Fortnight ${rel >= 0 ? '+' : ''}${rel}`}
                >
                  {col % 2 === 0 ? label : '\u00a0'}
                </button>
              ))}
              <div className="requirements-timeline-viewed-marker" style={{ left: `${(TIMELINE_CENTER / TIMELINE_COLS) * 100}%` }} />
            </div>
          </div>

          {bars.map(({ req, leftPct, widthPct, status }) => (
            <div key={req.id} className="requirements-timeline-row">
              <div className="requirements-timeline-label-col" title={req.name}>
                {req.name}
              </div>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    <div>{req.name}</div>
                    <div>{formatCurrency(calculateFortnightlyAmount(req))} / fortnight</div>
                    <div>{formatEffectiveRange(req)}</div>
                  </Tooltip>
                }
              >
                <div className="requirements-timeline-track">
                  <div
                    className={`requirements-timeline-bar status-${status}`}
                    style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                  />
                  <div
                    className="requirements-timeline-viewed-marker"
                    style={{ left: `${(TIMELINE_CENTER / TIMELINE_COLS) * 100}%` }}
                  />
                </div>
              </OverlayTrigger>
            </div>
          ))}
        </div>

        <div className="requirements-timeline-legend mt-2 small text-muted">
          <span className="legend-swatch status-active" /> Active in viewed fortnight
          <span className="legend-swatch status-not_yet ms-3" /> Not yet active
          <span className="legend-swatch status-ended ms-3" /> Ended
        </div>
      </div>
    </div>
  );
};

export default RequirementsTimeline;
